import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ShoppingBag, Store } from "lucide-react";

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register(form);
      navigate(u.role === "seller" ? "/dashboard" : "/");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const RoleBtn = ({ role, icon: Icon, label }) => (
    <button type="button" onClick={() => setForm({ ...form, role })} data-testid={`role-${role}`}
      className={`flex-1 flex flex-col items-center gap-2 rounded-2xl border p-4 transition-colors ${
        form.role === role ? "border-[#0EA5E9] bg-sky-50 text-[#0EA5E9]" : "border-[#E2E8F0] text-[#475569] hover:border-[#0EA5E9]/40"
      }`}>
      <Icon className="h-6 w-6" />
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[#E2E8F0] rounded-3xl p-8">
        <div className="flex items-center gap-2 text-[#0EA5E9] mb-2"><Sparkles className="h-5 w-5" /></div>
        <h1 className="font-display text-3xl font-extrabold text-[#0F172A] leading-tight">{t("auth.registerTitle")}</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="mb-2 block">{t("auth.role")}</Label>
            <div className="flex gap-3">
              <RoleBtn role="buyer" icon={ShoppingBag} label={t("auth.roleBuyer")} />
              <RoleBtn role="seller" icon={Store} label={t("auth.roleSeller")} />
            </div>
          </div>
          <div>
            <Label>{t("auth.name")}</Label>
            <Input value={form.name} onChange={set("name")} required data-testid="register-name" className="mt-1.5 rounded-xl" />
          </div>
          <div>
            <Label>{t("auth.email")}</Label>
            <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="mt-1.5 rounded-xl" />
          </div>
          <div>
            <Label>{t("auth.password")}</Label>
            <Input type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" className="mt-1.5 rounded-xl" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit"
            className="w-full rounded-full h-11 bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("auth.registerBtn")}
          </Button>
        </form>
        <p className="text-sm text-[#475569] mt-5 text-center">
          {t("auth.hasAccount")} <Link to="/login" className="text-[#0EA5E9] font-semibold">{t("nav.login")}</Link>
        </p>
      </motion.div>
    </div>
  );
}
